import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useState, type ChangeEvent, type FormEvent } from 'react';
import { getListLogsQueryKey, useDeleteLog, useListLogs, useUpdateLog } from '@/api/logs/logs';
import { getGetDailySummaryQueryKey, useGetDailySummary } from '@/api/summary/summary';
import type { FoodLogDto, UpdateLogDto } from '@/api/model';
import { MedicalDisclaimer } from '@/app/layout';
import { ManualAdd } from '@/components/manual-add';
import { BudgetRing } from '@/components/budget-ring';
import { Button } from '@/components/ui/button';
import { Card, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/field';
import { kcal } from '@/lib/utils';

/** Below this the resolver was unsure, and the row says so (spec §10). */
const CONFIRM_THRESHOLD = 0.6;

export function Today() {
  const queryClient = useQueryClient();
  const summary = useGetDailySummary();
  const logs = useListLogs();

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetDailySummaryQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListLogsQueryKey() }),
    ]);
  };

  const remove = useDeleteLog({ mutation: { onSuccess: refresh } });
  const update = useUpdateLog({ mutation: { onSuccess: refresh } });

  return (
    <>
      <Card className="flex flex-col items-center gap-3 py-6">
        <BudgetRing
          consumed={summary.data?.consumed ?? 0}
          budget={summary.data?.budget ?? null}
          pending={summary.isPending}
        />
        {summary.data?.budget == null && !summary.isPending && (
          <p className="text-center text-xs text-ink-muted">
            ยังไม่ได้ตั้งเป้าหมาย — ตั้งที่แท็บ &ldquo;เป้าหมาย&rdquo; เพื่อดูงบแคลอรีต่อวัน
          </p>
        )}
      </Card>

      <Card className="grid grid-cols-3 gap-2 text-center">
        <Macro label="โปรตีน" grams={summary.data?.protein} />
        <Macro label="คาร์บ" grams={summary.data?.carb} />
        <Macro label="ไขมัน" grams={summary.data?.fat} />
      </Card>

      <Card className="space-y-3">
        <CardTitle>มื้อวันนี้</CardTitle>

        {logs.isPending && <p className="text-sm text-ink-muted">กำลังโหลด…</p>}

        {logs.data?.length === 0 && (
          <p className="text-sm text-ink-muted">
            ยังไม่มีรายการ — พิมพ์ชื่ออาหารไปที่ LINE ได้เลย เช่น &ldquo;ข้าวผัด 1 จาน&rdquo;
          </p>
        )}

        <ul className="divide-y divide-border-subtle">
          {logs.data?.map((log) => (
            <LogRow
              key={log.id}
              log={log}
              onDelete={() => remove.mutate({ id: log.id })}
              deleting={remove.isPending && remove.variables?.id === log.id}
              onSave={async (data) => {
                await update.mutateAsync({ id: log.id, data });
              }}
            />
          ))}
        </ul>

        <ManualAdd onAdded={refresh} />
      </Card>

      <MedicalDisclaimer />
    </>
  );
}

function Macro({ label, grams }: { label: string; grams?: number }) {
  return (
    <div>
      <p className="text-xs text-ink-muted">{label}</p>
      <p className="text-lg font-semibold tabular-nums">
        {grams == null ? '—' : `${Math.round(grams)} g`}
      </p>
    </div>
  );
}

function LogRow({
  log,
  onDelete,
  deleting,
  onSave,
}: {
  log: FoodLogDto;
  onDelete: () => void;
  deleting: boolean;
  onSave: (data: UpdateLogDto) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const unconfirmed = log.confidence != null && log.confidence < CONFIRM_THRESHOLD;

  if (editing) {
    return (
      <li className="py-2.5">
        <EditForm log={log} onCancel={() => setEditing(false)} onSave={onSave} />
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {log.name}
          {log.qty !== 1 && <span className="text-ink-muted"> ×{log.qty}</span>}
        </p>
        {unconfirmed && <p className="text-xs text-warning">ค่าประมาณ — แตะดินสอเพื่อแก้</p>}
      </div>
      <p className="shrink-0 text-sm tabular-nums">{kcal(log.kcal)} kcal</p>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setEditing(true)}
        aria-label={`แก้ไข ${log.name}`}
      >
        <Pencil className="size-4" aria-hidden />
      </Button>
      <Button
        variant="danger"
        size="icon"
        onClick={onDelete}
        disabled={deleting}
        aria-label={`ลบ ${log.name}`}
      >
        <Trash2 className="size-4" aria-hidden />
      </Button>
    </li>
  );
}

/**
 * Inline correction for one item.
 *
 * Fields start from the logged values and are sent as absolute numbers, so a
 * user only has to retype the one that is wrong.
 */
function EditForm({
  log,
  onCancel,
  onSave,
}: {
  log: FoodLogDto;
  onCancel: () => void;
  onSave: (data: UpdateLogDto) => Promise<void>;
}) {
  const [values, setValues] = useState({
    kcal: String(Math.round(log.kcal)),
    protein: String(Math.round(log.protein)),
    carb: String(Math.round(log.carb)),
    fat: String(Math.round(log.fat)),
  });
  const [saving, setSaving] = useState(false);

  const set = (key: keyof typeof values) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues((current) => ({ ...current, [key]: event.target.value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onSave({
        kcal: Number(values.kcal),
        protein: Number(values.protein),
        carb: Number(values.carb),
        fat: Number(values.fat),
      });
      onCancel();
    } finally {
      setSaving(false);
    }
  };

  const complete = Object.values(values).every((value) => value !== '' && Number(value) >= 0);

  return (
    <form onSubmit={submit} className="space-y-2">
      <p className="truncate text-sm font-medium">{log.name}</p>
      <div className="grid grid-cols-4 gap-2">
        <NumberField label="kcal" value={values.kcal} onChange={set('kcal')} />
        <NumberField label="โปรตีน" value={values.protein} onChange={set('protein')} />
        <NumberField label="คาร์บ" value={values.carb} onChange={set('carb')} />
        <NumberField label="ไขมัน" value={values.fat} onChange={set('fat')} />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving || !complete}>
          {saving ? 'กำลังบันทึก…' : 'บันทึก'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          ยกเลิก
        </Button>
      </div>
    </form>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <label className="space-y-1">
      <span className="block text-caption text-ink-muted">{label}</span>
      <Input
        type="number"
        inputMode="decimal"
        min={0}
        value={value}
        onChange={onChange}
        className="h-[34px] px-2.5 text-body-sm tabular-nums"
      />
    </label>
  );
}
